import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Btn, Card, Select, Input, Empty, Spinner, Badge } from '../../components/shared/UI';
import api from '../../utils/api';
import { CASE_STATUS_COLORS, CASE_PRIORITY_COLORS, CASE_CATEGORIES } from '../Support';

const STATUS_LABELS = {
  open: 'Open', in_progress: 'In progress', waiting_on_user: 'Waiting on user',
  resolved: 'Resolved', closed: 'Closed',
};

export default function AdminSupportCases() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fStatus, setFStatus] = useState('active'); // active = not resolved/closed
  const [fCategory, setFCategory] = useState('');
  const [fPriority, setFPriority] = useState('');

  useEffect(() => {
    api.get('/admin/support').then(r => setCases(r.data)).finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cases.filter(c =>
      (fStatus === '' ? true
        : fStatus === 'active' ? !['resolved', 'closed'].includes(c.status)
        : c.status === fStatus) &&
      (!fCategory || c.category === fCategory) &&
      (!fPriority || c.priority === fPriority) &&
      (!q || c.subject.toLowerCase().includes(q) || c.caseNumber.toLowerCase().includes(q)
        || (c.user?.name || '').toLowerCase().includes(q) || (c.user?.email || '').toLowerCase().includes(q))
    );
  }, [cases, search, fStatus, fCategory, fPriority]);

  const needsReply = cases.filter(c => c.lastReplyBy === 'user' && !['resolved', 'closed'].includes(c.status)).length;

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader title="Support Cases"
        subtitle={`${cases.length} case${cases.length !== 1 ? 's' : ''}${needsReply ? ` · ${needsReply} awaiting your reply` : ''}`} />

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Search subject, case number, user…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
        <Select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ width: 160 }}>
          <option value="active">Active (unresolved)</option>
          <option value="">All statuses</option>
          {Object.keys(STATUS_LABELS).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </Select>
        <Select value={fCategory} onChange={e => setFCategory(e.target.value)} style={{ width: 140 }}>
          <option value="">All categories</option>
          {CASE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </Select>
        <Select value={fPriority} onChange={e => setFPriority(e.target.value)} style={{ width: 120 }}>
          <option value="">All priorities</option>
          {['high', 'normal', 'low'].map(p => <option key={p} value={p}>{p}</option>)}
        </Select>
      </div>

      {visible.length === 0
        ? <Empty icon="🎧" message="No support cases match" />
        : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                    <th style={th}>Case</th>
                    <th style={th}>User</th>
                    <th style={th}>Category</th>
                    <th style={th}>Priority</th>
                    <th style={th}>Status</th>
                    <th style={th}>Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c, i) => (
                    <tr key={c._id} onClick={() => navigate(`/admin/support/${c._id}`)} style={{
                      borderBottom: '1px solid var(--border-light)',
                      background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)',
                      cursor: 'pointer',
                    }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                          {c.lastReplyBy === 'user' && !['resolved', 'closed'].includes(c.status) && (
                            <span title="Awaiting your reply" style={{
                              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                              background: 'var(--danger)', marginInlineEnd: 8, verticalAlign: 'middle',
                            }} />
                          )}
                          {c.subject}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.caseNumber}</div>
                      </td>
                      <td style={td}>
                        <div style={{ color: 'var(--text)' }}>{c.user?.name || '—'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.user?.email}</div>
                      </td>
                      <td style={td}>{c.category}</td>
                      <td style={td}><Badge label={c.priority} color={CASE_PRIORITY_COLORS[c.priority]} /></td>
                      <td style={td}><Badge label={STATUS_LABELS[c.status]} color={CASE_STATUS_COLORS[c.status]} /></td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 13, color: 'var(--text-muted)' }}>
                        {new Date(c.lastActivityAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
    </div>
  );
}

const th = { padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' };
const td = { padding: '13px 16px', fontSize: 14, color: 'var(--text)', verticalAlign: 'middle' };
